import React from "react";
import { Form, OverlayTrigger, Popover, PopoverBody } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

import URLVerificationService from "services/URLVerificationService";
import Toast from "services/Toast";
import Emitter from "services/Emitter";

export const TABLE_COLUMNS = [
    {
        field: "created_at",
        label: "Date",
        width: "160px",
        style: { minWidth: "160px" },
        sortable: true,
        searchable: true,
    },
    {
        field: "product_brand",
        label: "Brand",
        width: "150px",
        style: { minWidth: "150px" },
        sortable: true,
        searchable: true,
    },
    {
        field: "product_title",
        label: "Title",
        width: "350px",
        style: { minWidth: "350px" },
        sortable: true,
        searchable: true,
    },
    {
        field: "product_upc",
        label: "UPC",
        width: "130px",
        style: { minWidth: "130px" },
        sortable: true,
        searchable: true,
    },
    {
        field: "product_sku",
        label: "SKU",
        width: "100px",
        style: { minWidth: "100px" },
        sortable: true,
        searchable: true,
    },
    {
        field: "website_name",
        label: "Website",
        width: "180px",
        style: { minWidth: "180px" },
        sortable: true,
        searchable: true,
    },
    {
        field: "score",
        label: "Score",
        width: "120px",
        style: { minWidth: "120px" },
        sortable: true,
        searchable: false,
        allowEmpty: false,
        options: [
            { label: "All", value: "all" },
            { label: "≤ 30", value: "0_30" },
            { label: "31-70", value: "31_70" },
        ],
    },
    {
        field: "notes",
        label: "Notes",
        width: "60px",
        style: { minWidth: "60px" },
        sortable: false,
        searchable: false,
    },
    {
        field: "status",
        label: "Status",
        width: "180px",
        style: { minWidth: "180px" },
        sortable: false,
        searchable: false,
    },
    {
        field: "updated_at",
        label: "Updated At",
        width: "160px",
        style: { minWidth: "160px" },
        sortable: true,
        searchable: true,
    },
    {
        field: "added_by_email",
        label: "Added By",
        width: "200px",
        style: { minWidth: "200px" },
        sortable: true,
        searchable: true,
    },
    {
        field: "source",
        label: "Source",
        width: "220px",
        style: { minWidth: "220px" },
        sortable: true,
        searchable: true,
    },
    {
        field: "updated_by",
        label: "Updated By",
        width: "130px",
        style: { minWidth: "130px" },
        sortable: true,
        searchable: true,
    },
    {
        field: "url",
        label: "Link",
        width: "60px",
        style: { minWidth: "60px" },
        sortable: false,
        searchable: false,
    },
];

export const BULK_ACTIONS = {
    APPROVE: "approved",
    REJECT: "rejected",
    UNSET: "unset",
};

export const DEFAULT_SORT = {
    field: "date",
    order: "desc",
};

export const COUNT_EVENTS = {
    ACTION_REQUIRED: "URL_VERIFICATION_ACTION_REQUIRED_COUNT",
    APPROVED: "URL_VERIFICATION_APPROVED_COUNT",
    REJECTED: "URL_VERIFICATION_REJECTED_COUNT",
};

export const EXPORT_EVENTS = {
    ACTION_REQUIRED: "URL_VERIFICATION_ACTION_REQUIRED_EXPORT",
    APPROVED: "URL_VERIFICATION_APPROVED_EXPORT",
    REJECTED: "URL_VERIFICATION_REJECTED_EXPORT",
};

export const REFRESH_EVENTS = {
    ACTION_REQUIRED: "URL_VERIFICATION_ACTION_REQUIRED_REFRESH",
    APPROVED: "URL_VERIFICATION_APPROVED_REFRESH",
    REJECTED: "URL_VERIFICATION_REJECTED_REFRESH",
    ALL: "URL_VERIFICATION_REFRESH_ALL",
};

export const filterAndSort = (items, setup) => {
    let filtered = [...items];

    if (setup.search && typeof setup.search === "object") {
        Object.keys(setup.search).forEach((field) => {
            const value = setup.search[field];
            if (!value || value === "all") {
                return;
            }

            if (field === "score") {
                filtered = filtered.filter((row) => {
                    const score = Number(row.score);
                    if (value === "0_30") {
                        return score <= 30;
                    }
                    if (value === "31_70") {
                        return score >= 31 && score <= 70;
                    }
                    return true;
                });
                return;
            }

            if (field === "source") {
                const searchLower = String(value).toLowerCase();
                filtered = filtered.filter((row) => {
                    const label =
                        ORIGIN_LABELS[row.origin] ?? row.origin ?? "—";
                    const cellValue =
                        row.origin === "manual" && row.source_note
                            ? `${label} · ${row.source_note}`
                            : label;
                    return cellValue.toLowerCase().includes(searchLower);
                });
                return;
            }

            const searchLower = String(value).toLowerCase();
            filtered = filtered.filter((row) => {
                const cellValue = row[field];
                if (cellValue === null || cellValue === undefined) {
                    return false;
                }
                return String(cellValue).toLowerCase().includes(searchLower);
            });
        });
    }

    if (setup.sort && setup.sort.field) {
        const { field, order } = setup.sort;
        const getSortValue = (row) => {
            if (field === "source") {
                const label = ORIGIN_LABELS[row.origin] ?? row.origin ?? "—";
                return row.origin === "manual" && row.source_note
                    ? `${label} · ${row.source_note}`
                    : label;
            }
            return row[field] ?? "";
        };
        filtered.sort((a, b) => {
            const valA = getSortValue(a);
            const valB = getSortValue(b);
            const cmp = String(valA).localeCompare(String(valB), undefined, {
                numeric: true,
            });
            return order === "asc" ? cmp : -cmp;
        });
    }

    const total = filtered.length;
    const start = (setup.page - 1) * setup.pageSize;
    const pageRows = filtered.slice(start, start + setup.pageSize);

    return { rows: pageRows, total };
};

export const STATUS_OPTIONS = [
    { label: "Select status", value: "" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
];

export const handleStatusChange = (rowId, newStatus) => {
    const statusToSend = newStatus || "unset";

    return URLVerificationService.updateStatus([rowId], statusToSend).then(
        (result) => {
            if (result) {
                Toast.ok(`Status updated to ${statusToSend}`);
                Emitter.emit(REFRESH_EVENTS.ALL);
                return true;
            }
            Toast.error("Failed to update status");
            return false;
        }
    );
};

const ORIGIN_LABELS = {
    manual: "URL Uploader",
    npd: "NPD Process",
    pdd: "PDD Process",
    v1: "V1 Process",
    ng: "NG Process",
    direct: "Direct",
    "google-shopping": "Google Shopping",
};

export const RENDER_COLUMNS = {
    score: (row) => {
        const score = row.score;
        const color = score <= 30 ? "red" : "orange";
        return <span style={{ color, fontWeight: "bold" }}>{score}%</span>;
    },
    status: (row) => (
        <Form.Select
            size="sm"
            value={row.status === "unset" ? "" : row.status}
            onChange={(e) => handleStatusChange(row.id, e.target.value)}
            onClick={(e) => e.stopPropagation()}
        >
            {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </Form.Select>
    ),
    notes: (row) => {
        if (!row.notes) return null;
        return (
            <div style={{ textAlign: "center" }}>
                <OverlayTrigger
                    placement="left"
                    overlay={
                        <Popover style={{ maxWidth: "400px" }}>
                            <PopoverBody>{row.notes}</PopoverBody>
                        </Popover>
                    }
                >
                    <span style={{ cursor: "pointer" }}>&#9432;</span>
                </OverlayTrigger>
            </div>
        );
    },
    source: (row) => {
        const label = ORIGIN_LABELS[row.origin] ?? row.origin ?? "—";
        if (row.origin === "manual" && row.source_note) {
            return (
                <span>
                    {label} &middot; {row.source_note}
                </span>
            );
        }
        return <span>{label}</span>;
    },
    url: (row) => (
        <a
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            title="Open link"
        >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
        </a>
    ),
};
